/**
 * Action Handler Registry — Phase 2b
 *
 * Maps action IDs (from actions.json) to executable functions.
 * Decouples button definitions (data) from implementation (code).
 *
 * Pattern: `buildActionHandlers(deps)` returns a bound handler map.
 * Each handler receives an optional params object and returns void.
 *
 * Handlers delegate to current recording, upload, export, and document flows.
 * New handlers require a code change here; new button arrangements using
 * existing handlers only need actions.json edits.
 *
 * See: docs/features/infrastructure/app-shell.md#action-handler-registry
 */

/**
 * Build a map of action handlers bound to the current runtime context.
 *
 * @param {Object} deps - Runtime dependencies
 * @param {Object} deps.session - From useRecordingSession()
 * @param {Function} deps.navigate - React Router navigate()
 * @param {string} deps.patientId - Current patient ID
 * @param {string} deps.userId - Current user ID
 * @param {Object} deps.dropdownState - Document type dropdown state
 * @param {Function} deps.openActivityBar - From AppShellContext
 * @param {Function} deps.setIsProcessing - Processing state setter
 * @returns {Object} Map of { handlerName: (params?) => void }
 */
export function buildActionHandlers(deps = {}) {
    const {
        session,
        navigate,
        patientId,
        userId,
        dropdownState,
        openActivityBar,
        setIsProcessing,
    } = deps;

    return {
        // ─── Recording ─────────────────────────────────────────
        startRecording: () => {
            if (!patientId) {
                // No patient context — redirect to patient page for quick-add
                navigate?.("/patient", { state: { pendingAction: "record" } });
                return;
            }
            // Route recording through the same pending-action path as upload.
            // ActivityBar waits for session.isReady before calling requestStart,
            // so an early click cannot race document-type initialization and
            // leave the user without a selectable recording type.
            if (openActivityBar) {
                openActivityBar("record");
                return;
            }

            // Defensive fallback for callers outside AppShell. Only start when
            // the session is already initialized; never recreate the race.
            if (session?.isReady && session?.requestStart) {
                session.requestStart();
                return;
            }

            console.warn("[actionHandlers] recording session is not ready");
        },

        stopRecording: () => {
            session?.stop?.();
        },

        cancelRecording: () => {
            session?.requestCancel?.();
        },

        pauseRecording: () => {
            session?.pauseRecording?.();
        },

        resumeRecording: () => {
            session?.resumeRecording?.();
        },

        // ─── Upload ────────────────────────────────────────────
        startUpload: () => {
            if (!patientId) {
                // No patient context — redirect to patient page for quick-add
                navigate?.("/patient", { state: { pendingAction: "upload" } });
                return;
            }
            // Open the activity bar with upload action — the InputRow file picker
            // or drag-drop area handles the actual file selection
            openActivityBar?.("upload");
        },

        // ─── Navigation (from document editor → patient page) ──
        recordOnPatientPage: () => {
            if (patientId && navigate) {
                navigate("/patient", { state: { patientId, pendingAction: "record" } });
            }
        },

        uploadOnPatientPage: () => {
            if (patientId && navigate) {
                navigate("/patient", { state: { patientId, pendingAction: "upload" } });
            }
        },

        // ─── Document ──────────────────────────────────────────
        createDocument: () => {
            // Navigate to new document creation for this patient
            if (patientId && navigate) {
                navigate("/document/new", { state: { patientId } });
            } else {
                console.warn("[actionHandlers] createDocument requires patientId and navigate");
            }
        },

        editPatient: () => {
            // Dispatches an event that the patient edit modal listens for
            window.dispatchEvent(new CustomEvent("openEditPatient", {
                detail: { patientId },
            }));
        },

        sharePatient: () => {
            // Dispatches an event that the sharing modal listens for
            window.dispatchEvent(new CustomEvent("openSharePatient", {
                detail: { patientId },
            }));
        },

        viewHistory: () => {
            // Scroll to or navigate to history section
            if (patientId && navigate) {
                navigate(`/patient/${patientId}`, { state: { scrollTo: "history" } });
            }
        },

        // ─── Processing ────────────────────────────────────────
        reprocessAll: () => {
            window.dispatchEvent(new CustomEvent("initiateReprocess", {
                detail: { isReprocess: true, shouldAutoStart: true },
            }));
        },

        reprocessSection: (params) => {
            window.dispatchEvent(new CustomEvent("initiateReprocess", {
                detail: {
                    isReprocess: true,
                    shouldAutoStart: true,
                    section: params?.section,
                },
            }));
        },

        viewProgress: () => {
            // Focus the status band — just ensure the activity bar is open
            openActivityBar?.("progress");
        },

        regenerateDocument: () => {
            window.dispatchEvent(new CustomEvent("initiateReprocess", {
                detail: { isReprocess: true, shouldAutoStart: true, regenerate: true },
            }));
        },

        // ─── Document Save ─────────────────────────────────────
        saveDocument: () => {
            window.dispatchEvent(new CustomEvent("triggerSaveDocument"));
        },

        // ─── Document Delete ──────────────────────────────────
        deleteDocument: () => {
            window.dispatchEvent(new CustomEvent("triggerDeleteDocument"));
        },

        // ─── Export Note (header-style) ────────────────────────
        exportNote: () => {
            window.dispatchEvent(new CustomEvent("triggerExportNote"));
        },

        // ─── Transcript ────────────────────────────────────────
        editTranscript: () => {
            window.dispatchEvent(new CustomEvent("openTranscriptEditor"));
        },

        // ─── Patient ───────────────────────────────────────────
        addPatient: () => {
            // Dispatches an event that the AddEditPatient modal listens for
            window.dispatchEvent(new CustomEvent("openAddPatient"));
        },

        viewAllPatients: () => {
            if (navigate) {
                navigate("/patients");
            }
        },

        // ─── Account ───────────────────────────────────────────
        updateAccount: () => {
            if (navigate) {
                navigate("/account");
            }
        },
    };
}

/**
 * Validate that all handler names referenced in actions.json exist in the registry.
 * Call at dev time or in tests — not in production hot path.
 *
 * @param {Object} actionsConfig - Parsed actions.json
 * @param {Object} handlers - Result of buildActionHandlers()
 * @returns {string[]} Array of missing handler names (empty = all valid)
 */
export function validateHandlerBindings(actionsConfig, handlers) {
    const missing = [];
    for (const [context, config] of Object.entries(actionsConfig)) {
        for (const button of config.buttons || []) {
            if (button.handler && !handlers[button.handler]) {
                missing.push(`${context}.buttons.${button.id} → handler "${button.handler}" not found`);
            }
            // Check state swap buttons too
            if (button.stateSwap?.buttons) {
                for (const swapBtn of button.stateSwap.buttons) {
                    if (swapBtn.handler && !handlers[swapBtn.handler]) {
                        missing.push(`${context}.buttons.${button.id}.stateSwap.${swapBtn.id} → handler "${swapBtn.handler}" not found`);
                    }
                }
            }
        }
        for (const preset of config.chatPresets || []) {
            if (preset.handler && !handlers[preset.handler]) {
                missing.push(`${context}.chatPresets.${preset.id} → handler "${preset.handler}" not found`);
            }
        }
    }
    return missing;
}
